import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getWidget } from './widgetRegistry';
import { createEventBus } from './eventBus';
import WidgetErrorBoundary from './WidgetErrorBoundary';
import { LayoutContextProvider, useLayoutCtx } from './LayoutContext';
import { BUILTIN_LAYOUTS } from './builtinLayouts';
import { createScriptSandbox } from './scriptSandbox';
import { sdkHelpers } from './sdkHelpers';
import { buildCtxForInvoke } from './sdkRuntime';

// Register built-in widgets at module import time. The barrel below is
// side-effect-only — each widget module calls registerWidget(...) when loaded.
import './widgets/index';

const ROOT_STYLE = { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' };
const FALLBACK_STYLE = { padding: '24px', color: 'var(--text-secondary)', fontSize: '13px' };

function authHeaders(token) { return { Authorization: `Bearer ${token}` }; }

/**
 * <LayoutRenderer> — single entry point for every category-bound surface in
 * the Case Client. On mount it resolves the right Layout for
 * (category, caseTypeId?, roleId?, stepName?, taskTypeId?) via
 * GET /layouts/resolve, falls back to the in-memory BUILTIN_* tree on
 * `{layout: null}`, and recursively mounts each widget by type from the
 * registry. Re-resolves when category-narrowing keys change.
 *
 * Phase L4a — events_json wire executor
 * -------------------------------------
 * The resolved layout's `events_json` is a list of wires
 *   { id, from: { widget_id, topic }, to: { widget_id, action }, enabled? }.
 * On mount we subscribe each enabled wire's `from.topic` on the per-renderer
 * bus and, when the source widget publishes that topic, dispatch the named
 * action on the target widget via its registered handler. Source-widget
 * matching uses a `_sourceWidgetId` tag the per-widget bus injects on every
 * publish — see WidgetMount below.
 */
export default function LayoutRenderer({
  category,
  // resolver narrowing keys
  caseTypeId = null,
  roleId = null,
  stepName = null,
  taskTypeId = null,
  // shared layout context — passed to every widget
  token,
  currentUser,
  workspaceId,
  role,
  case: caseRow = null,
  workItem = null,
  draft = null,
  setDraft = null,
  callbacks = null,
  // override path (e.g. designer "Preview" pane mounts a tree directly)
  treeOverride = null,
  // optional override of the resolved layout's events_json — used by the
  // Designer's Preview pane so wire changes are testable without saving.
  eventsOverride = null,
}) {
  const [resolved, setResolved] = useState(null); // {layout, events, source} | 'pending'
  const [error, setError] = useState(null);

  // Per-renderer event bus instance. Stable for the lifetime of this mount.
  const busRef = useRef(null);
  if (busRef.current == null) busRef.current = createEventBus();

  // Per-widget action registry — Map<widgetId, RefObject<{[action]: handler}>>.
  // Populated by useWidgetActions; read by the wire executor.
  const actionsByWidget = useRef(new Map());

  // Phase L4b — lazy single iframe sandbox shared by every Script Widget on
  // this renderer plus every wire's optional `transform_js`. Created on first
  // demand so layouts with no scripts pay zero overhead.
  const sandboxRef = useRef(null);
  const ctxRef = useRef(null);

  const ensureSandbox = useCallback(() => {
    if (sandboxRef.current) return sandboxRef.current;
    if (typeof document === 'undefined') return null;
    const sb = createScriptSandbox({
      onBusPublish: (topic, payload, sourceWidgetId) => {
        const bus = busRef.current;
        if (!bus) return;
        const tagged = {
          ...(payload && typeof payload === 'object' ? payload : {}),
          _sourceWidgetId: sourceWidgetId || null,
        };
        bus.publish(topic, tagged);
      },
      onInvokeAction: (widgetId, action, payload) => {
        const ref = actionsByWidget.current.get(widgetId);
        const handler = ref?.current?.[action];
        if (typeof handler === 'function') {
          try { handler(payload); } catch (e) {
            if (typeof console !== 'undefined') console.warn(`[wire] sandbox→${widgetId}.${action}`, e);
          }
        }
      },
      onFetchRequest: async (msg) => {
        const ctx = ctxRef.current;
        if (!ctx?.token || !ctx?.workspaceId) return { ok: false, error: 'No auth context for /script-fetch' };
        try {
          const r = await axios.post(
            `${API_BASE_URL}/api/casematrix/workspaces/${ctx.workspaceId}/script-fetch`,
            { url: msg.url, method: msg.method, headers: msg.headers, body: msg.body, timeout_ms: msg.timeout_ms },
            { headers: authHeaders(ctx.token) },
          );
          return { ok: !!r.data?.ok, status: r.data?.status, headers: r.data?.headers, text: r.data?.text, json: r.data?.json };
        } catch (e) {
          return { ok: false, error: e?.response?.data?.detail || e?.message || 'fetch failed' };
        }
      },
      onLog: (level, args, sourceWidgetId) => {
        if (typeof console === 'undefined') return;
        const tag = sourceWidgetId ? `[script:${sourceWidgetId}]` : '[script]';
        const fn = console[level] || console.log;
        try { fn(tag, ...(Array.isArray(args) ? args : [args])); } catch (_) { /* ignore */ }
      },
    });
    sandboxRef.current = sb;
    if (sb) sb.setHelpers(sdkHelpers);
    return sb;
  }, []);

  useEffect(() => () => {
    if (sandboxRef.current) {
      try { sandboxRef.current.destroy(); } catch (_) { /* ignore */ }
      sandboxRef.current = null;
    }
  }, []);

  const registerActions = useCallback((widgetId, handlersRef) => {
    actionsByWidget.current.set(widgetId, handlersRef);
    return () => {
      // Only delete if we still own the slot — protects against the tail of a
      // re-mount cycle clobbering a fresh registration.
      if (actionsByWidget.current.get(widgetId) === handlersRef) {
        actionsByWidget.current.delete(widgetId);
      }
    };
  }, []);

  const invokeAction = useCallback((widgetId, action, payload) => {
    const ref = actionsByWidget.current.get(widgetId);
    const handler = ref?.current?.[action];
    if (typeof handler !== 'function') return undefined;
    try {
      return handler(payload);
    } catch (e) {
      if (typeof console !== 'undefined') {
        console.error(`[wire] ${widgetId}.${action} threw:`, e);
      }
      return undefined;
    }
  }, []);

  // Resolve. Phase L1: no DB layouts exist — always fall back to BUILTIN_*.
  // Phase L2 wires GET /layouts/resolve. We keep the endpoint call commented
  // out here under a feature flag so the L1 build is byte-equivalent to today.
  useEffect(() => {
    if (treeOverride) {
      setResolved({ layout: treeOverride, events: eventsOverride || [], source: 'override' });
      return;
    }
    let cancelled = false;
    setResolved('pending');
    setError(null);

    (async () => {
      try {
        if (!token || !workspaceId) {
          if (!cancelled) setResolved({ layout: null, events: [], source: 'no-auth' });
          return;
        }
        const params = { category };
        if (caseTypeId) params.case_type_id = caseTypeId;
        if (roleId) params.role_assignment_id = roleId;
        if (stepName) params.step_name = stepName;
        if (taskTypeId) params.task_type_id = taskTypeId;

        const url = `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/layouts/resolve`;
        const r = await axios.get(url, { headers: authHeaders(token), params });
        if (cancelled) return;
        // Resolver wraps the tree as {layout: {id, name, version, tree_json,
        // events_json, ...}, source}. We want both the renderable tree and
        // the wire list — pull each out of the wrapper.
        const wrapper = r.data && r.data.layout ? r.data.layout : null;
        const layout = wrapper && wrapper.tree_json ? wrapper.tree_json : null;
        const events = wrapper && Array.isArray(wrapper.events_json) ? wrapper.events_json : [];
        setResolved({ layout, events, source: layout ? 'db' : 'builtin' });
      } catch (e) {
        // 404 = endpoint not deployed yet (Phase L1) → silently fall back.
        // Other errors fall back too but surface in dev console.
        if (cancelled) return;
        if (e?.response?.status && e.response.status !== 404) {
          if (typeof console !== 'undefined') console.warn('[LayoutRenderer] resolve failed:', e?.message);
        }
        setResolved({ layout: null, events: [], source: 'fallback' });
      }
    })();

    return () => { cancelled = true; };
  }, [category, caseTypeId, roleId, stepName, taskTypeId, token, workspaceId, treeOverride, eventsOverride]);

  const tree = useMemo(() => {
    const isValid = (n) => n && typeof n === 'object' && typeof n.type === 'string' && n.type;
    if (treeOverride) return isValid(treeOverride) ? treeOverride : null;
    if (resolved && resolved !== 'pending' && isValid(resolved.layout)) return resolved.layout;
    // Resolved but malformed (no `type`) → fall back to builtin so the worker
    // surface keeps working even if a customer saved a corrupt tree.
    return BUILTIN_LAYOUTS[category] || null;
  }, [resolved, treeOverride, category]);

  const events = useMemo(() => {
    if (treeOverride) return Array.isArray(eventsOverride) ? eventsOverride : [];
    if (resolved && resolved !== 'pending' && Array.isArray(resolved.events)) return resolved.events;
    return [];
  }, [resolved, treeOverride, eventsOverride]);

  // Wire executor — for each enabled wire, subscribe to its source topic on
  // the bus and invoke the target action when the source widget publishes.
  // Phase L4b — if the wire defines `transform_js`, the payload is piped
  // through the iframe sandbox (kind='transform') before dispatch. The sandbox
  // is lazy-created here on first transform need.
  useEffect(() => {
    const bus = busRef.current;
    if (!bus || !Array.isArray(events) || events.length === 0) return undefined;

    const unsubs = [];
    for (const wire of events) {
      if (!wire || wire.enabled === false) continue;
      const topic = wire.from?.topic;
      const sourceId = wire.from?.widget_id;
      const targetId = wire.to?.widget_id;
      const action = wire.to?.action;
      const transformJs = typeof wire.transform_js === 'string' ? wire.transform_js.trim() : '';
      if (!topic || !sourceId || !targetId || !action) continue;

      const off = bus.subscribe(topic, async (payload) => {
        if (payload?._sourceWidgetId !== sourceId) return;
        let dispatchPayload = payload;
        if (transformJs) {
          const sb = ensureSandbox();
          if (sb) {
            try {
              const ctxData = buildCtxForInvoke(ctxRef.current || {}, { id: sourceId });
              const transformed = await sb.invoke('transform', transformJs, payload, ctxData, {
                source_widget_id: sourceId,
                timeout_ms: 1500,
              });
              dispatchPayload = transformed == null ? payload : transformed;
            } catch (e) {
              if (typeof console !== 'undefined') console.warn(`[wire ${wire.id || ''}] transform_js failed:`, e?.message || e);
              return; // a failed transform must not silently dispatch the unmapped payload
            }
          }
        }
        invokeAction(targetId, action, dispatchPayload);
      });
      unsubs.push(off);
    }
    return () => { unsubs.forEach(fn => { try { fn(); } catch { /* ignore */ } }); };
  }, [events, invokeAction, ensureSandbox]);

  const ctx = useMemo(() => {
    const value = {
      token,
      currentUser,
      workspaceId,
      role,
      category,
      case: caseRow,
      workItem,
      draft,
      setDraft,
      bus: busRef.current,
      callbacks: callbacks || {},
      registerActions,
      invokeAction,
      // Phase L4b — Script Widgets and wire transforms invoke through this.
      get scriptSandbox() { return ensureSandbox(); },
      // ctx data factory for sandbox invocations. Captures live case/workItem/draft
      // by reading the ctxRef snapshot the renderer keeps in sync (below).
      buildSandboxCtx: (sourceWidget) => buildCtxForInvoke(ctxRef.current || {}, sourceWidget || null),
    };
    return value;
  }, [token, currentUser, workspaceId, role, category, caseRow, workItem, draft, setDraft, callbacks, registerActions, invokeAction, ensureSandbox]);

  // Keep ctxRef pointed at the latest ctx so sandbox handlers (created once
  // and long-lived) read live token/workspaceId/case/etc. instead of closing
  // over the snapshot from the first render.
  ctxRef.current = ctx;

  if (resolved === 'pending') {
    return <div style={FALLBACK_STYLE}>Loading layout…</div>;
  }
  if (error) {
    return <div style={FALLBACK_STYLE}>Layout failed to load. {String(error)}</div>;
  }
  if (!tree) {
    return <div style={FALLBACK_STYLE}>No layout configured for category "{category}".</div>;
  }

  return (
    <div style={ROOT_STYLE} data-layout-category={category} data-layout-source={resolved?.source || 'builtin'}>
      <LayoutContextProvider value={ctx}>
        <WidgetNode node={tree} />
      </LayoutContextProvider>
    </div>
  );
}

/**
 * Walks one node of the widget tree. Recursion happens via children: for a
 * container widget the registered Component receives a `children` prop that
 * is already-rendered <WidgetNode> elements; non-containers ignore children.
 *
 * Each WidgetNode shadows the inherited LayoutContext with a per-widget bus
 * that auto-tags publishes with `_sourceWidgetId = node.id`. That tag is what
 * the wire executor uses to filter "this publish came from the source widget
 * the wire cares about." Direct calls to the parent bus (no widget context)
 * lack the tag and naturally skip wire dispatch.
 */
export function WidgetNode({ node }) {
  if (!node || typeof node !== 'object') return null;
  const meta = getWidget(node.type);
  if (!meta || typeof meta.Component !== 'function') {
    return (
      <div style={{ padding: '12px', border: '1px dashed var(--border-color)', color: 'var(--text-secondary)', fontSize: '12px', borderRadius: '6px' }}>
        Unknown widget type "{node.type}". The plugin that provides it may not be installed.
      </div>
    );
  }
  return <WidgetMount node={node} meta={meta} />;
}

function WidgetMount({ node, meta }) {
  const parentCtx = useLayoutCtx();
  const widgetCtx = useMemo(() => {
    const parentBus = parentCtx.bus;
    const taggedBus = parentBus
      ? {
          publish: (topic, payload) => parentBus.publish(topic, {
            ...(payload && typeof payload === 'object' ? payload : {}),
            _sourceWidgetId: node.id,
          }),
          subscribe: parentBus.subscribe.bind(parentBus),
          unsubscribe: parentBus.unsubscribe.bind(parentBus),
          allTopics: parentBus.allTopics.bind(parentBus),
          clear: parentBus.clear.bind(parentBus),
        }
      : parentBus;
    return { ...parentCtx, bus: taggedBus };
  }, [parentCtx, node.id]);

  const Component = meta.Component;
  const children = Array.isArray(node.children) && node.children.length
    ? node.children.map((c, i) => <WidgetNode key={c.id || `${c.type}-${i}`} node={c} />)
    : null;

  return (
    <LayoutContextProvider value={widgetCtx}>
      <WidgetErrorBoundary widgetType={node.type} paletteLabel={meta.palette_label}>
        <Component node={node} config={node.config || {}}>
          {children}
        </Component>
      </WidgetErrorBoundary>
    </LayoutContextProvider>
  );
}
